/**
 * Calls to the API Worker's /crew routes (job packs for crew). Job pack reads are saved on the phone and shown when
 * there is no signal; actions that can wait (signing, checklists, reports) are saved to send later (modules/offline).
 */
import { API_URL, ApiError, FALLBACK, apiRequest, isNetworkError, noteError, QUEUED_MESSAGE, type AdminRequestOptions } from "../lib/api";
import { supabase } from "../lib/supabase";
import { queueable, readFileThrough, readThrough } from "./offline/cache";
import { bodyFrom, enqueue, newActionId, type QueuedAction, type SendResult } from "./offline/outbox";

const PACK_BASE = "/crew" as const;

const offlineNow = () => typeof navigator !== "undefined" && navigator.onLine === false;
const noSignal = () => new ApiError(0, "No signal. Check your connection and try again.", "network");


/**
 * Call a /crew route (job packs for crew) on the API Worker. Job pack reads are saved on the phone and shown when
 * there is no signal; actions that can wait (signing, checklists, reports) are saved to send later.
 */
export async function crewFetch<T>(path: string, init: RequestInit = {}, opts: AdminRequestOptions = {}): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase();
  if (method === "GET") {
    return readThrough<T>(path, async () => {
      if (offlineNow()) throw noSignal();
      return (await (await apiRequest(PACK_BASE, path, init, opts)).json()) as T;
    }, isNetworkError);
  }
  const q = queueable(method, path, typeof init.body === "string" ? init.body : null);
  if (!q) return (await (await apiRequest(PACK_BASE, path, init, opts)).json()) as T;
  // Every action that can wait has its own id, so sending it again (after a timeout) is safe.
  const id = newActionId();
  const queuedAt = new Date().toISOString();
  const keyed = { ...init, headers: { ...(init.headers as Record<string, string> | undefined), "idempotency-key": id } };
  if (!offlineNow()) {
    try {
      return (await (await apiRequest(PACK_BASE, path, keyed, opts)).json()) as T;
    } catch (e) {
      if (!isNetworkError(e)) throw e;
    }
  }
  // Show it as done on the saved pack first, so the screen reloads with it.
  await q.patch().catch(() => undefined);
  await enqueue({ id, path, method, body: await bodyFrom(init), label: q.label, bookingId: q.bookingId, queuedAt }, method === "PUT");
  throw new ApiError(0, QUEUED_MESSAGE, "queued", { actionId: id });
}

/** Queue an action that needs an earlier queued action's answer, e.g. "/incidents/{<id>.incident.id}/photos". */
export async function queueAfter(path: string, init: RequestInit, label: string, bookingId: string | null): Promise<void> {
  await enqueue({ id: newActionId(), path, method: (init.method ?? "POST").toUpperCase(), body: await bodyFrom(init), label, bookingId });
}

/** Send one saved action (used by the outbox): the status and JSON answer, never throwing on a refusal. */
export async function sendQueued(a: QueuedAction, path: string, body: BodyInit | undefined): Promise<SendResult> {
  if (offlineNow()) throw noSignal();
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  const json = a.body.kind === "json";
  const res = await fetch(`${API_URL}${PACK_BASE}${path}`, {
    method: a.method,
    body,
    headers: { ...(json ? { "content-type": "application/json" } : {}), ...(token ? { authorization: `Bearer ${token}` } : {}), "idempotency-key": a.id, "x-queued-at": a.queuedAt },
  });
  let parsed: unknown = null;
  try {
    parsed = await res.json();
  } catch {
    parsed = null;
  }
  const message = (parsed as { message?: string } | null)?.message ?? FALLBACK[res.status];
  if (!res.ok) noteError(res.status, `${PACK_BASE}${path}`, message ?? `Request failed (${res.status}).`);
  return { status: res.status, json: parsed, message };
}

/** Fetch a binary /crew resource (a job document). Documents are saved on the phone for use offline. */
export async function crewBlob(path: string, opts: AdminRequestOptions = {}): Promise<Blob> {
  return readFileThrough(path, async () => {
    if (offlineNow()) throw noSignal();
    return (await apiRequest(PACK_BASE, path, {}, opts)).blob();
  }, isNetworkError);
}
